// src/components/GuideModal.jsx
import React, { useState, useRef, useEffect } from "react";
import "./css/GuideModal.css";
import { db, auth } from "../firebase";
import { doc, setDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import { normalizeSlug } from "../utils/slug";

export const GUIDE_JSON = {
    title: "Marseille",
    slug: "marseille",

    country: {
        type: "country",
        name: "France",
        code: "FR",
        continent: "Europe",
        currency: "EUR",
        language: "French",
        flag: "🇫🇷"
    },

    city: {
        type: "city",
        name: "Marseille",
        state: "Provence-Alpes-Côte d’Azur",
        population: "870K",
        timezone: "CET (UTC+1)"
    },

    guide: {
        id: "marseille",
        title: "Marseille City Guide",
        subtitle: "Mediterranean port city, culture, calanques & coastal life.",
        pill: "City Travel Guide",
        heroClass: "marseille",
        heroImage: "https://cdn.pixabay.com/photo/2022/08/02/08/39/church-7359849_1280.jpg"
    },

    quickFacts: {
        region: "Provence-Alpes-Côte d’Azur",
        bestMonths: "May–Jun, Sep",
        idealDuration: "2–4 days",
        dailyBudgetRange: "€80–200",
        walkability: "Moderate (some hills, good transit)"
    },

    sections: {
        whyVisit: {
            title: "Why visit Marseille?",
            description:
                "Marseille is France’s oldest city and most multicultural. It blends historic ports, bold street life, stunning coastal landscapes, and gateway access to the Calanques National Park."
        },

        bestTimeToVisit: {
            title: "Best time to visit",
            seasons: [
                { name: "Spring", emoji: "🌸", months: "Apr–Jun", note: "Perfect weather & fewer crowds" },
                { name: "Summer", emoji: "☀️", months: "Jul–Aug", note: "Beach season & festivals" },
                { name: "Autumn", emoji: "🍁", months: "Sep–Oct", note: "Warm sea & calmer city" },
                { name: "Winter", emoji: "❄️", months: "Nov–Mar", note: "Mild climate & local vibe" }
            ]
        },

        neighborhoods: {
            title: "Popular neighborhoods",
            areas: [
                { name: "Vieux-Port", emoji: "⚓", highlights: "Historic harbor & cafés" },
                { name: "Le Panier", emoji: "🎨", highlights: "Old town & street art" },
                { name: "La Corniche", emoji: "🌊", highlights: "Seaside walks & views" },
                { name: "Cours Julien", emoji: "🎶", highlights: "Creative scene & nightlife" }
            ]
        },

        budget: {
            title: "Estimated daily budget",
            tiers: [
                { type: "Backpacker", range: "€80–110" },
                { type: "Mid-range", range: "€130–200", highlight: true },
                { type: "Luxury", range: "€280+" }
            ]
        },

        gettingAround: {
            title: "Getting around Marseille",
            description:
                "Marseille has metro, tram, and bus lines covering most areas. Walking works in central districts, while boats and buses reach beaches and calanques."
        },

        cta: {
            title: "Exploring Marseille?",
            subtitle: "Save this guide or add your Mediterranean escape.",
            actions: ["Add Trip", "Save Guide"]
        }
    },

    travelGuide: {
        wifiAndInternet: {
            title: "Wi-Fi & Internet",
            tips: [
                "Wi-Fi available in cafés and hotels",
                "Public Wi-Fi near the Old Port",
                "Strong mobile coverage city-wide"
            ]
        },

        simAndEsim: {
            title: "SIM & eSIM",
            options: [
                "SIM cards available at airport and city shops",
                "eSIMs ideal for short stays",
                "Top providers: Orange, SFR, Bouygues, Free"
            ],
            tip: "Useful for coastal navigation and transport"
        },

        moneyAndPayments: {
            title: "Money & Payments",
            currency: "Euro (€)",
            tips: [
                "Cards widely accepted",
                "Small bars may prefer cash",
                "ATMs easy to find in central areas"
            ]
        },

        transportAndCabs: {
            title: "Transport & Cabs",
            tips: [
                "Metro and trams are affordable",
                "Uber operates city-wide",
                "Boats connect nearby islands"
            ]
        },

        essentialApps: {
            title: "Best Apps to Install",
            categories: [
                {
                    category: "Transport",
                    apps: ["Google Maps", "Citymapper", "Uber"]
                },
                {
                    category: "Outdoors",
                    apps: ["AllTrails", "Komoot"]
                },
                {
                    category: "Travel",
                    apps: ["Google Translate", "Tripadvisor"]
                },
                {
                    category: "Payments",
                    apps: ["Wise", "Revolut"]
                }
            ]
        },

        safetyAndTips: {
            title: "Safety & Local Tips",
            tips: [
                "Stay aware around busy transport hubs",
                "Avoid isolated areas late at night",
                "Book Calanques access in advance (summer)",
                "Try bouillabaisse by the port"
            ]
        }
    },
    gallery: [
        "https://cdn.pixabay.com/photo/2017/06/17/08/30/marseille-2411528_1280.jpg",
        "https://cdn.pixabay.com/photo/2016/04/17/01/45/marseille-1334093_1280.jpg",
        "https://cdn.pixabay.com/photo/2020/07/30/21/35/building-5451341_1280.jpg",
        "https://cdn.pixabay.com/photo/2020/06/07/20/11/marseille-5271931_1280.jpg"
    ],
    placesToVisit: [
        {
            label: "Vieux-Port",
            placeId: "vieux-port-marseille"
        },
        {
            label: "Basilique Notre-Dame de la Garde",
            placeId: "notre-dame-de-la-garde"
        },
        {
            label: "Le Panier",
            placeId: "le-panier"
        },
        {
            label: "Calanques National Park",
            placeId: "calanques-national-park"
        },
        {
            label: "Château d’If",
            placeId: "chateau-dif"
        },
        {
            label: "La Corniche",
            placeId: "la-corniche"
        },
        {
            label: "Cours Julien",
            placeId: "cours-julien"
        },
        {
            label: "Frioul Islands",
            placeId: "frioul-islands"
        }
    ]

};


const PLACES_FOR_UPLOAD = [
    {
        id: "vieux-port-marseille",
        name: "Vieux-Port",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "harbor",
        shortDescription: "Historic harbor and lively heart of Marseille, lined with cafés and boats.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning or sunset",
        entryFee: "Free",
        location: { lat: 43.2965, lng: 5.3698 },
        images: [],
        tips: [
            "Visit the fish market in the morning",
            "Great sunset photography spot",
            "Start point for boat tours"
        ]
    },

    {
        id: "notre-dame-de-la-garde",
        name: "Basilique Notre-Dame de la Garde",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "basilica",
        shortDescription: "Hilltop basilica offering panoramic views over Marseille and the Mediterranean.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Late afternoon",
        entryFee: "Free",
        location: { lat: 43.2840, lng: 5.3710 },
        images: [],
        tips: [
            "Best panoramic view in Marseille",
            "Accessible by bus or hike",
            "Beautiful interior mosaics"
        ]
    },

    {
        id: "le-panier",
        name: "Le Panier",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "neighborhood",
        shortDescription: "Oldest district in Marseille with narrow streets, colorful houses, and street art.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Free",
        location: { lat: 43.3007, lng: 5.3673 },
        images: [],
        tips: [
            "Explore hidden courtyards",
            "Visit artisan shops",
            "Great for photography"
        ]
    },

    {
        id: "calanques-national-park",
        name: "Calanques National Park",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "national_park",
        shortDescription: "Spectacular limestone cliffs and turquoise coves ideal for hiking and swimming.",
        recommendedTime: "Half or full day",
        bestTimeToVisit: "Spring or early autumn",
        entryFee: "Free",
        location: { lat: 43.2125, lng: 5.4300 },
        images: [],
        tips: [
            "Bring water and sunscreen",
            "Access may be restricted in peak summer",
            "Boat tours offer alternative views"
        ]
    },

    {
        id: "chateau-dif",
        name: "Château d’If",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "historic_site",
        shortDescription: "Island fortress made famous by 'The Count of Monte Cristo'.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 43.2813, lng: 5.3254 },
        images: [],
        tips: [
            "Accessible by boat from Vieux-Port",
            "Check ferry schedules",
            "Combine with Frioul Islands"
        ]
    },

    {
        id: "la-corniche",
        name: "La Corniche",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "scenic_drive",
        shortDescription: "Seaside road offering stunning coastal views and beach access.",
        recommendedTime: "1 hour",
        bestTimeToVisit: "Sunset",
        entryFee: "Free",
        location: { lat: 43.2784, lng: 5.3545 },
        images: [],
        tips: [
            "Ideal for evening walks",
            "Stop at Vallon des Auffes",
            "Beautiful Mediterranean views"
        ]
    },

    {
        id: "cours-julien",
        name: "Cours Julien",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "neighborhood",
        shortDescription: "Creative district known for street art, independent shops, and nightlife.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Evening",
        entryFee: "Free",
        location: { lat: 43.2968, lng: 5.3830 },
        images: [],
        tips: [
            "Best nightlife area",
            "Colorful murals everywhere",
            "Great local cafés and bars"
        ]
    },

    {
        id: "frioul-islands",
        name: "Frioul Islands",
        city: "marseille",
        country: "france",
        guidesId: "marseille",
        type: "island",
        shortDescription: "Island archipelago with beaches, hiking trails, and crystal-clear waters.",
        recommendedTime: "Half day",
        bestTimeToVisit: "Summer",
        entryFee: "Free (boat fare required)",
        location: { lat: 43.2716, lng: 5.2980 },
        images: [],
        tips: [
            "Take sunscreen and water",
            "Ideal for swimming and snorkeling",
            "Combine with Château d’If visit"
        ]
    }
];


export default function GuideModal({ open, onClose, currentUserId }) {
    const titleRef = useRef(null);
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [guideJsonInput, setGuideJsonInput] = useState("");
    const [placesJsonInput, setPlacesJsonInput] = useState("");


    useEffect(() => {
        if (open) {
            setTimeout(() => titleRef.current?.focus(), 50);
        }
    }, [open]);

    if (!open) return null;

    const resetForm = () => {
        setTitle("");
        setSlug("");
        setCountry("");
        setGuideJsonInput("");
        setPlacesJsonInput("");
        setError(null);
        setSaving(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSave = async () => {
        if (!guideJsonInput.trim() && !placesJsonInput.trim()) {
            setError("Nothing to save");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (guideJsonInput.trim()) {
                await uploadGuide();
            }

            if (placesJsonInput.trim()) {
                await uploadPlaces();
            }

            alert("Saved successfully ✅");
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };
    const uploadGuide = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error("You must be logged in");
            }

            if (!guideJsonInput.trim()) {
                throw new Error("Guide JSON is empty");
            }

            setSaving(true);
            setError(null);

            const parsedJSON = JSON.parse(guideJsonInput);
            const normalizedSlug = normalizeSlug(
                slug || parsedJSON.slug || title
            );

            await setDoc(
                doc(db, "guides", normalizedSlug),
                {
                    ...parsedJSON,
                    id: normalizedSlug,
                    slug: normalizedSlug,
                    createdBy: auth.currentUser.uid,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to upload guide");
            throw err; // important so handleSave knows it failed
        } finally {
            setSaving(false);
        }
    };

    const uploadPlaces = async () => {
        if (!auth.currentUser) {
            throw new Error("You must be logged in");
        }

        if (!placesJsonInput.trim()) {
            throw new Error("Places JSON is empty");
        }

        const places = JSON.parse(placesJsonInput);
        if (!Array.isArray(places)) {
            throw new Error("Places JSON must be an array");
        }

        const batch = writeBatch(db);

        places.forEach((place) => {
            if (!place.id || !place.name) {
                throw new Error("Each place must have id and name");
            }

            const placeSlug = normalizeSlug(place.slug || place.name);

            // 1️⃣ Save place document
            batch.set(
                doc(db, "places", place.id),
                {
                    ...place,
                    slug: placeSlug, // ✅ add slug
                    createdBy: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false }
            );

            // 2️⃣ Register slug
            batch.set(
                doc(db, "slugs", placeSlug),
                {
                    placeId: place.id, // ✅ new supported type
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );
        });

        await batch.commit();
    };

    const loadGuideJSON = (json) => {
        try {
            setTitle(json.title || "");
            setSlug(json.slug || json.id || "");
            setCountry(json.country?.name || "");
            setGuideJsonInput(JSON.stringify(json, null, 2));
            setError(null);
        } catch {
            setError("Invalid Guide JSON");
        }
    };
    const loadPlacesJSON = (placesArray) => {
        if (!Array.isArray(placesArray)) {
            setError("Places JSON must be an array");
            return;
        }
        setPlacesJsonInput(JSON.stringify(placesArray, null, 2));
    };

    return (
        <div
            className="modal-backdrop"
            onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="modal guide-modal">
                <h3>Create Guide</h3>

                {/* Title */}
                <label>Title *</label>
                <input
                    ref={titleRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="France Travel Guide"
                />

                {/* Slug */}
                <label>Slug (optional)</label>
                <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="france"
                />

                {/* Country */}
                <label>Country</label>
                <input
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="France"
                />

                {/* JSON */}
                <label>Guide JSON *</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={guideJsonInput}
                    onChange={(e) => setGuideJsonInput(e.target.value)}
                    placeholder="{ ...full guide json... }"
                />

                <label>Places JSON</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={placesJsonInput}
                    onChange={(e) => setPlacesJsonInput(e.target.value)}
                    placeholder="[ { ...place }, { ...place } ]"
                />

                {error && <div className="error">{error}</div>}

                <div className="controls">
                    <button className="btn-secondary" onClick={handleClose}>
                        Cancel
                    </button>
                    <div className="json-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadGuideJSON(GUIDE_JSON)}
                        >
                            📘 Load Guide JSON
                        </button>

                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadPlacesJSON(PLACES_FOR_UPLOAD)}
                        >
                            📍 Load Places JSON
                        </button>
                    </div>

                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Guide"}
                    </button>


                </div>
            </div>
        </div>
    );
}
